import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useHoldOrder } from '../hooks/use-order-mutations';
import {
  holdOrderSchema,
  type HoldOrderFormInput,
  type HoldOrderFormOutput,
} from '../schemas/hold-schema';
import { orderConflictKey } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['reason']);

export interface HoldDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Parks fulfilment with a reason for the timeline; release is a single click on the page. */
export function HoldDialog({ storeId, order, open, onOpenChange }: HoldDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const hold = useHoldOrder(storeId);
  const form = useForm<HoldOrderFormInput, unknown, HoldOrderFormOutput>({
    resolver: useI18nResolver(holdOrderSchema),
    defaultValues: { reason: '' },
  });

  const close = () => {
    onOpenChange(false);
    hold.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await hold.mutateAsync({ id: order.id, body: values });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const conflict = orderConflictKey(error, 'orders:hold.errors.conflict');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('orders:hold.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:hold.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:hold.reason')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t('orders:hold.reasonPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={hold.isPending}>
                {hold.isPending ? t('orders:hold.submitting') : t('orders:hold.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
